/**
 * useSimulation - Hook pour gérer les simulations
 *
 * Orchestre le lancement, la progression et l'affichage des résultats
 * d'une simulation de réponses.
 */

import { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import type {
  SimulationConfig,
  SimulationResult,
  SimulationContext,
} from "../types/simulation";
import { simulate } from "../lib/simulation/SimulationService";
import { consumeSimulationCredits } from "../lib/quotaTracking";
import {
  hashSimulationInput,
  getCachedResult,
  getCacheMeta,
  setCachedResult,
} from "../lib/simulation/SimulationCacheService";

interface UseSimulationOptions {
  /** ID du poll */
  pollId: string;

  /** Questions du poll */
  questions: Array<{
    id: string;
    title: string;
    type: "single" | "multiple" | "text" | "long-text" | "matrix";
    required?: boolean;
    options?: Array<{ id: string; label: string }>;
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    matrixType?: "single" | "multiple";
  }>;

  /** Contexte détecté */
  detectedContext: SimulationContext;
}

interface UseSimulationReturn {
  /** État de la simulation */
  isRunning: boolean;

  /** Résultat de la simulation */
  result: SimulationResult | null;

  /** Progression (0-100) */
  progress: number;

  /** Nombre de réponses générées */
  currentResponses: number;

  /** Volume configuré */
  configuredVolume: number;

  /** Temps écoulé (ms) */
  elapsedTime: number;

  /** Lancer une simulation (opts.force = ignorer le cache et relancer un vrai appel) */
  startSimulation: (
    config: SimulationConfig,
    opts?: { force?: boolean },
  ) => Promise<void>;

  /** Réinitialiser */
  reset: () => void;

  /** Erreur éventuelle */
  error: Error | null;

  /** Le résultat courant provient-il du cache (pas d'appel IA) ? */
  fromCache: boolean;

  /** Horodatage (epoch ms) de mise en cache du résultat affiché, si caché. */
  cachedAt: number | null;
}

export function useSimulation({
  pollId,
  questions,
  detectedContext,
}: UseSimulationOptions): UseSimulationReturn {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentResponses, setCurrentResponses] = useState(0);
  const [configuredVolume, setConfiguredVolume] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [cachedAt, setCachedAt] = useState<number | null>(null);

  const startSimulation = useCallback(
    async (config: SimulationConfig, opts?: { force?: boolean }) => {
      setIsRunning(true);
      setProgress(0);
      setCurrentResponses(0);
      setConfiguredVolume(config.volume);
      setElapsedTime(0);
      setError(null);
      setResult(null);
      setFromCache(false);
      setCachedAt(null);

      // Cache par hash : si les questions/objectif/contexte n'ont pas changé,
      // on sert le résultat caché sans relancer d'appel IA ni consommer de crédit.
      const hash = hashSimulationInput(config, questions);
      if (!opts?.force) {
        const cached = getCachedResult(hash);
        if (cached) {
          const meta = getCacheMeta(hash);
          setProgress(100);
          setCurrentResponses(cached.respondents.length);
          setElapsedTime(cached.generationTime);
          setResult(cached);
          setFromCache(true);
          setCachedAt(meta?.cachedAt ?? null);
          setIsRunning(false);
          return;
        }
      }

      const startTime = Date.now();
      let progressInterval: NodeJS.Timeout;

      try {
        // Tick du temps écoulé uniquement (la progression est désormais réelle,
        // pilotée par onProgress ci-dessous, pas estimée par interval).
        progressInterval = setInterval(() => {
          setElapsedTime(Date.now() - startTime);
        }, 100);

        // Lancer la simulation. onProgress reflète la vraie génération
        // répondant-par-répondant (X/total). On plafonne à 95 % : les 5 %
        // restants couvrent la phase d'analyse/validation post-génération.
        const simulationResult = await simulate(
          config,
          questions,
          (completed, total) => {
            setCurrentResponses(completed);
            setProgress(Math.min(Math.round((completed / total) * 95), 95));
          },
        );

        const validationFailed = Boolean(
          simulationResult.objectiveValidation?.error,
        );

        // Mettre en cache (invalidé au prochain changement de questions/objectif).
        // Ne PAS cacher si la validation IA a échoué : sinon un nouvel essai
        // resservirait l'erreur au lieu de relancer l'appel Gemini.
        if (!validationFailed) {
          setCachedResult(hash, simulationResult, Date.now());
        }

        // Consommer les crédits seulement si la simulation a abouti (validation
        // IA OK). Sinon l'utilisateur paierait un quota pour une analyse
        // « indisponible » (régression 2026-06-18 : quota décrémenté pour rien).
        if (!validationFailed) {
          consumeSimulationCredits(user?.id, pollId, simulationResult.id);
        }

        // Arrêter l'interval
        clearInterval(progressInterval);

        // Finaliser
        setProgress(100);
        setCurrentResponses(simulationResult.respondents.length);
        setElapsedTime(simulationResult.generationTime);
        setResult(simulationResult);
      } catch (err) {
        clearInterval(progressInterval!);
        setError(err instanceof Error ? err : new Error("Erreur inconnue"));
      } finally {
        setIsRunning(false);
      }
    },
    [questions, pollId, user?.id],
  );

  const reset = useCallback(() => {
    setIsRunning(false);
    setResult(null);
    setProgress(0);
    setCurrentResponses(0);
    setElapsedTime(0);
    setError(null);
    setFromCache(false);
    setCachedAt(null);
  }, []);

  return {
    isRunning,
    result,
    progress,
    currentResponses,
    configuredVolume,
    elapsedTime,
    startSimulation,
    reset,
    error,
    fromCache,
    cachedAt,
  };
}
